import { IArticleFields } from 'generated/types/contentful'
import { Entry } from 'contentful'
import type { Document } from '@contentful/rich-text-types'
import { parseTopic, Topic } from 'services/ContentfulService/models/topics'
import { Author, DefaultAuthorImage, parseAuthor } from 'services/ContentfulService/models/author'
import { getEnv } from 'lib/envWrapper'
import { parseProduct, Product } from './product'
import { Asset, enhanceAsset, parseAsset } from './asset'
import { ContentfulEntity, defaultSysObject, parseSystemFields, removeFalsy } from 'services/ContentfulService/utils'
import { getArticleCategory } from 'experiences/learn/utils'
import { calculateArticleReadingTime } from 'components/vendor/Contentful/richText/utils'

const env = getEnv()
export interface ArticleSummary {
	slug: string
	id: string
	primaryImage: Asset
	title: string
	description: string
	dateWritten: string
	readingTime: string
	category: string
	relativePath: string
	absoluteUrl: string
}

export interface Article extends ContentfulEntity {
	id: string

	/** Title */
	title: string

	/** Slug */
	slug: string

	/** featured */
	featured?: boolean | undefined

	/** Subtitle */
	subtitle?: string | undefined

	/** Product */
	products: Product[]

	/** Tags */
	tags: string[] | undefined

	/** Author */
	author: Author

	/** Topics */
	topics: Topic[]

	/** Top Image */
	primaryImage: Asset

	/** Date Written */
	dateWritten?: string | undefined

	dateWrittenObject?: Date

	/** Description */
	description: string

	/** Content */
	content: Document

	/** Related Articles */

	/** metadata */
	metadata: Record<string, unknown>

	collaborationDetails: CollaborationDetails

	relativePath: string

	absoluteUrl: string
}

interface IArticleInstance extends Article {
	getRelatedArticles: () => Promise<Article[]>
	updateFavorite: (favorite: boolean) => Promise<void>
	isFavorite: boolean
}

export const ArticleInstance = (article: Article): IArticleInstance => {
	return {
		...article,
		getRelatedArticles: () => Promise.resolve([]),
		isFavorite: false,
		updateFavorite: () => Promise.resolve()
	}
}

interface CollaborationDetails {
	partner: any
	collaboratorIsPrimaryAuthor: boolean
}

const defaultAuthor: Author = {
	sys: defaultSysObject,
	id: '',
	firstName: 'Fiona',
	lastName: 'Staff',
	image: DefaultAuthorImage
}

export const parseArticle = (contentfulArticle: Entry<IArticleFields>): Article => {
	const fields = contentfulArticle.fields
	const relativePath = `/learn/articles/${fields.slug}`
	const date = fields.dateWritten ? new Date(fields.dateWritten) : undefined
	const sys = parseSystemFields(contentfulArticle.sys)
	if (date) {
		sys.createdAt = date
	}
	const primaryImage = parseAsset(fields.primaryImage)
	primaryImage.fit = 'fill'
	return {
		id: contentfulArticle.sys.id,
		dateWritten: date?.toLocaleDateString() || 'Recently',
		dateWrittenObject: date,
		description: fields.description || '',
		relativePath,
		absoluteUrl: `${env.root_domain}${relativePath}`,
		title: fields.title,
		slug: fields.slug,
		author: fields.author ? parseAuthor(fields.author) : defaultAuthor,
		collaborationDetails: {
			collaboratorIsPrimaryAuthor: false,
			partner: {}
		},
		content: fields.content,
		featured: fields.featured || false,
		metadata: {},
		primaryImage: primaryImage,
		products: (fields.product || []).map(t => parseProduct(t, true)),
		subtitle: fields.subtitle,
		tags: fields.tags || [],
		topics: removeFalsy((fields.topics || []).map(t => parseTopic(t, true))),
		sys
	}
}

export const summarizeArticle = (article: Article): ArticleSummary => {
	return {
		dateWritten: article.dateWritten,
		description: article.description,
		category: getArticleCategory(article),
		id: article.id,
		relativePath: article.relativePath,
		primaryImage: article.primaryImage,
		slug: article.slug,
		title: article.title,
		readingTime: `${calculateArticleReadingTime(article.content)} min read`
	} as ArticleSummary
}

export const summarizeArticles = (articles: Article[]): ArticleSummary[] => articles.map(a => summarizeArticle(a))

export const sliceString = (value: string, sliceTo: number): string => {
	if (value.split(' ').length < sliceTo) {
		return value
	} else {
		return `${value.split(' ').slice(0, sliceTo).join(' ')} ...`
	}
}

export const sliceStringByChars = (value: string, sliceTo: number): string => {
	if (value.length < sliceTo) {
		return value
	} else {
		const sliceIndex = sliceTo - 10 + value.substring(sliceTo - 10, sliceTo).lastIndexOf(' ') || sliceTo
		return `${value.substring(0, sliceIndex)} ...`
	}
}

export const enhanceArticle = async (article: Article): Promise<Article> =>
	enhanceAsset(article.primaryImage).then(primaryImage => ({ ...article, primaryImage }))
